import React, { useEffect, useMemo, useState } from "react";
import { Plus, Trash2 } from "lucide-react";

import { Button } from "components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Checkbox } from "components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Input } from "components/ui/input";
import { Textarea } from "components/ui/textarea";
import { ScrollArea } from "components/ui/scroll-area";

const getDimensionsKey = (questionsJson) => {
  if (!questionsJson) return null;
  if (Array.isArray(questionsJson.dimensions)) return "dimensions";
  if (Array.isArray(questionsJson.dimension)) return "dimension";
  return null;
};

const deepClone = (obj) => JSON.parse(JSON.stringify(obj || {}));

const toLangFieldKey = (langCode) => {
  const upper = String(langCode || "").toUpperCase();
  if (upper === "EN") return "q";
  return upper.toLowerCase();
};

const collectExistingLangsFromQuestions = (questionsJson) => {
  const out = new Set();
  const dimKey = getDimensionsKey(questionsJson);
  const dims = (questionsJson && questionsJson[dimKey]) || [];
  dims.forEach((d) => {
    (d.factors || []).forEach((f) => {
      (f.questions || []).forEach((q) => {
        Object.keys(q || {}).forEach((key) => {
          if (
            key.length <= 3 &&
            key !== "q" &&
            key !== "id" &&
            key !== "role" &&
            key !== "reverse" &&
            key !== "importance"
          ) {
            out.add(key.toUpperCase());
          }
        });
      });
    });
  });
  return Array.from(out);
};

const getNextFactorId = (dimension) => {
  const ids = (dimension?.factors || []).map((f) => Number(f.id) || 0);
  return (ids.length ? Math.max(...ids) : 0) + 1;
};

const getNextQuestionOrderId = (questionsJson) => {
  const dimKey = getDimensionsKey(questionsJson);
  const dims = (questionsJson && questionsJson[dimKey]) || [];
  let maxId = 0;
  dims.forEach((d) => {
    (d.factors || []).forEach((f) => {
      (f.questions || []).forEach((q) => {
        const id = Number(q?.id);
        if (Number.isFinite(id)) maxId = Math.max(maxId, id);
      });
    });
  });
  return maxId + 1;
};

const renumberQuestionsSequentially = (questionsJson) => {
  const key = getDimensionsKey(questionsJson);
  if (!key) return questionsJson;
  const dims = questionsJson[key] || [];
  const flat = [];
  dims.forEach((dim, dIndex) => {
    (dim.factors || []).forEach((fac, fIndex) => {
      (fac.questions || []).forEach((q, qIndex) => {
        const idNum = Number(q?.id);
        flat.push({ dIndex, fIndex, qIndex, id: Number.isFinite(idNum) ? idNum : Infinity });
      });
    });
  });
  flat.sort((a, b) => {
    if (a.id !== b.id) return a.id - b.id;
    if (a.dIndex !== b.dIndex) return a.dIndex - b.dIndex;
    if (a.fIndex !== b.fIndex) return a.fIndex - b.fIndex;
    return a.qIndex - b.qIndex;
  });
  let next = 1;
  flat.forEach(({ dIndex, fIndex, qIndex }) => {
    dims[dIndex].factors[fIndex].questions[qIndex].id = next++;
  });
  return questionsJson;
};

export default function QuestionStructureManagerModal({ open, onClose, questions, onSave, languages }) {
  const [working, setWorking] = useState({});
  const [selectedDimIndex, setSelectedDimIndex] = useState(0);
  const [selectedFactorIndex, setSelectedFactorIndex] = useState(0);

  const dimKey = useMemo(() => getDimensionsKey(working), [working]);
  const dims = (working && working[dimKey]) || [];

  useEffect(() => {
    if (open) {
      const base = deepClone(questions || {});
      let key = getDimensionsKey(base);
      if (!key) {
        base.dimensions = [];
        key = "dimensions";
      }
      if (!Array.isArray(base[key])) base[key] = [];
      setWorking(base);
      setSelectedDimIndex(0);
      setSelectedFactorIndex(0);
    }
  }, [open, questions]);

  const effectiveLanguages = useMemo(() => {
    const explicitlyProvided = Array.isArray(languages) ? languages.map((l) => String(l).toUpperCase()) : [];
    const fromQuestions = collectExistingLangsFromQuestions(questions || {});
    const merged = new Set(["EN", ...explicitlyProvided, ...fromQuestions]);
    return Array.from(merged);
  }, [languages, questions]);

  const showFrenchFactorTitle = useMemo(() => {
    if (effectiveLanguages.includes("FR")) return true;
    const key = getDimensionsKey(working);
    const dims = (working && key && working[key]) || [];
    return (dims || []).some((d) => (d?.factors || []).some((f) => typeof f?.fr_title === "string" && f.fr_title.length > 0));
  }, [effectiveLanguages, working]);

  const languageFieldDefs = useMemo(() => {
    return effectiveLanguages.map((LC) => ({ code: LC, field: toLangFieldKey(LC) }));
  }, [effectiveLanguages]);

  const currentDim = dims?.[selectedDimIndex] || null;
  const currentFactors = Array.isArray(currentDim?.factors) ? currentDim.factors : [];
  const currentFactor = currentFactors?.[selectedFactorIndex] || null;
  const currentQuestions = Array.isArray(currentFactor?.questions) ? currentFactor.questions : [];

  const setDimField = (index, field, value) => {
    setWorking((prev) => {
      const next = deepClone(prev);
      if (!Array.isArray(next[dimKey])) next[dimKey] = [];
      const dim = { ...(next[dimKey][index] || {}) };
      dim[field] = value;
      next[dimKey][index] = dim;
      return next;
    });
  };

  const setFactorField = (dIndex, fIndex, field, value) => {
    setWorking((prev) => {
      const next = deepClone(prev);
      const dim = { ...(next[dimKey][dIndex] || {}) };
      const factors = Array.isArray(dim.factors) ? [...dim.factors] : [];
      const factor = { ...(factors[fIndex] || {}) };
      factor[field] = value;
      factors[fIndex] = factor;
      dim.factors = factors;
      next[dimKey][dIndex] = dim;
      return next;
    });
  };

  const setQuestionField = (dIndex, fIndex, qIndex, field, value) => {
    setWorking((prev) => {
      const next = deepClone(prev);
      const dim = { ...(next[dimKey][dIndex] || {}) };
      const factors = Array.isArray(dim.factors) ? [...dim.factors] : [];
      const factor = { ...(factors[fIndex] || {}) };
      const questionsArr = Array.isArray(factor.questions) ? [...factor.questions] : [];
      const q = { ...(questionsArr[qIndex] || {}) };
      q[field] = value;
      questionsArr[qIndex] = q;
      factor.questions = questionsArr;
      factors[fIndex] = factor;
      dim.factors = factors;
      next[dimKey][dIndex] = dim;
      return next;
    });
  };

  const addDimension = () => {
    setWorking((prev) => {
      const next = deepClone(prev);
      if (!Array.isArray(next[dimKey])) next[dimKey] = [];
      next[dimKey].push({ id: next[dimKey].length + 1, title: "", factors: [] });
      return next;
    });
    setSelectedDimIndex(dims.length);
    setSelectedFactorIndex(0);
  };

  const removeDimension = (index) => {
    setWorking((prev) => {
      const next = deepClone(prev);
      const arr = Array.isArray(next[dimKey]) ? [...next[dimKey]] : [];
      arr.splice(index, 1);
      next[dimKey] = arr;
      return next;
    });
    setSelectedDimIndex(0);
    setSelectedFactorIndex(0);
  };

  const addFactor = (dIndex) => {
    setWorking((prev) => {
      const next = deepClone(prev);
      const dim = { ...(next[dimKey][dIndex] || {}) };
      const factors = Array.isArray(dim.factors) ? [...dim.factors] : [];
      const newId = getNextFactorId(dim);
      factors.push({ id: newId, title: "", fr_title: "", questions: [] });
      dim.factors = factors;
      next[dimKey][dIndex] = dim;
      return next;
    });
    setSelectedFactorIndex(currentFactors.length);
  };

  const removeFactor = (dIndex, fIndex) => {
    setWorking((prev) => {
      const next = deepClone(prev);
      const dim = { ...(next[dimKey][dIndex] || {}) };
      const factors = Array.isArray(dim.factors) ? [...dim.factors] : [];
      factors.splice(fIndex, 1);
      dim.factors = factors;
      next[dimKey][dIndex] = dim;
      return next;
    });
    setSelectedFactorIndex(0);
  };

  const addQuestion = (dIndex, fIndex) => {
    setWorking((prev) => {
      const next = deepClone(prev);
      const dim = { ...(next[dimKey][dIndex] || {}) };
      const factors = Array.isArray(dim.factors) ? [...dim.factors] : [];
      const factor = { ...(factors[fIndex] || {}) };
      const questionsArr = Array.isArray(factor.questions) ? [...factor.questions] : [];
      const newId = getNextQuestionOrderId(next);
      const q = { id: newId, q: "", reverse: false, importance: 0 };
      languageFieldDefs.forEach(({ field }) => {
        if (field !== "q") q[field] = "";
      });
      questionsArr.push(q);
      factor.questions = questionsArr;
      factors[fIndex] = factor;
      dim.factors = factors;
      next[dimKey][dIndex] = dim;
      return next;
    });
  };

  const removeQuestion = (dIndex, fIndex, qIndex) => {
    setWorking((prev) => {
      const next = deepClone(prev);
      const dim = { ...(next[dimKey][dIndex] || {}) };
      const factors = Array.isArray(dim.factors) ? [...dim.factors] : [];
      const factor = { ...(factors[fIndex] || {}) };
      const questionsArr = Array.isArray(factor.questions) ? [...factor.questions] : [];
      questionsArr.splice(qIndex, 1);
      factor.questions = questionsArr;
      factors[fIndex] = factor;
      dim.factors = factors;
      next[dimKey][dIndex] = dim;
      return next;
    });
  };

  const handleSave = () => {
    const cleaned = deepClone(working);
    const key = getDimensionsKey(cleaned) || "dimensions";
    if (!Array.isArray(cleaned[key])) cleaned[key] = [];
    cleaned[key].forEach((d) => {
      if (!Array.isArray(d.factors)) d.factors = [];
      d.factors.forEach((f) => {
        if (!Array.isArray(f.questions)) f.questions = [];
      });
    });
    renumberQuestionsSequentially(cleaned);
    onSave && onSave(cleaned);
    onClose && onClose();
  };

  const handleClose = () => {
    onClose && onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && handleClose()}>
      <DialogContent className="h-[90dvh] w-full max-w-6xl overflow-hidden p-0">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle>Manage Structure</DialogTitle>
          <DialogDescription>
            Add or edit dimensions, factors, and questions including multilingual text.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col lg:flex-row gap-6 px-6 pb-6 min-h-0">
          <div className="lg:w-[360px] lg:shrink-0 flex flex-col min-h-0">
            <ScrollArea className="h-full pr-2">
              <div className="flex flex-col gap-6">
                <Card className="shadow-sm">
                  <CardHeader className="pb-4">
                    <CardTitle className="text-base">Dimensions</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-3">
                      {(dims || []).map((dim, i) => (
                        <div key={`dim-${i}`} className="flex items-center gap-2">
                          <Button
                            type="button"
                            size="sm"
                            variant={selectedDimIndex === i ? "default" : "outline"}
                            className="shrink-0"
                            onClick={() => {
                              setSelectedDimIndex(i);
                              setSelectedFactorIndex(0);
                            }}
                          >
                            Dim {i + 1}
                          </Button>
                          <Input
                            placeholder="Dimension title"
                            value={dim?.title || ""}
                            onChange={(e) => setDimField(i, "title", e.target.value)}
                          />
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            onClick={() => removeDimension(i)}
                            className="text-destructive"
                            aria-label="Remove dimension"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                    <Button variant="secondary" size="sm" onClick={addDimension} className="inline-flex items-center gap-2">
                      <Plus className="h-4 w-4" /> Add dimension
                    </Button>
                  </CardContent>
                </Card>

                <Card className="shadow-sm">
                  <CardHeader className="pb-4">
                    <CardTitle className="text-base">Factors</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-3">
                      {(currentFactors || []).map((f, fi) => (
                        <div key={`fac-${fi}`} className="flex items-start gap-2">
                          <Button
                            type="button"
                            size="sm"
                            variant={selectedFactorIndex === fi ? "default" : "outline"}
                            className="shrink-0"
                            onClick={() => setSelectedFactorIndex(fi)}
                          >
                            Factor {fi + 1}
                          </Button>
                          <div className="flex-1 space-y-2">
                            <Input
                              placeholder="Factor title"
                              value={f?.title || ""}
                              onChange={(e) => setFactorField(selectedDimIndex, fi, "title", e.target.value)}
                            />
                            {showFrenchFactorTitle && (
                              <Input
                                placeholder="Factor title (FR)"
                                value={f?.fr_title || ""}
                                onChange={(e) => setFactorField(selectedDimIndex, fi, "fr_title", e.target.value)}
                              />
                            )}
                          </div>
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            onClick={() => removeFactor(selectedDimIndex, fi)}
                            className="text-destructive"
                            aria-label="Remove factor"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => addFactor(selectedDimIndex)}
                      className="inline-flex items-center gap-2"
                    >
                      <Plus className="h-4 w-4" /> Add factor
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </ScrollArea>
          </div>

          <div className="flex-1 min-w-0 min-h-0 flex flex-col">
            <Card className="shadow-sm flex-1 flex flex-col overflow-hidden">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Questions</CardTitle>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => addQuestion(selectedDimIndex, selectedFactorIndex)}
                    className="inline-flex items-center gap-2"
                  >
                    <Plus className="h-4 w-4" /> Add question
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="flex-1 p-0 overflow-hidden">
                <ScrollArea className="h-full w-full pr-2">
                  <div className="space-y-4 p-6">
                    {(currentQuestions || []).length === 0 && (
                      <div className="rounded-md border border-dashed border-border/70 p-6 text-center text-sm text-muted-foreground">
                        No questions yet. Add one to get started.
                      </div>
                    )}
                    {(currentQuestions || []).map((q, qi) => (
                      <div key={`q-${qi}`} className="space-y-4 rounded-lg border border-border/60 bg-card/50 p-4">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-semibold text-foreground">
                            Question {qi + 1}{" "}
                            {Number.isFinite(Number(q?.id)) ? `(id ${q.id})` : ""}
                          </p>
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            onClick={() => removeQuestion(selectedDimIndex, selectedFactorIndex, qi)}
                            className="text-destructive"
                            aria-label="Remove question"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>

                        <div className="grid gap-4 md:grid-cols-2">
                          {languageFieldDefs.map(({ code, field }) => (
                            <label key={`lang-${code}`} className="flex flex-col gap-2 text-sm font-medium text-foreground">
                              <span>{code === "EN" ? "English" : code}</span>
                              <Textarea
                                placeholder={code === "EN" ? "Question (EN)" : `Translation (${code})`}
                                rows={code === "EN" ? 4 : 3}
                                value={(q && (q[field] ?? ""))}
                                onChange={(e) => setQuestionField(selectedDimIndex, selectedFactorIndex, qi, field, e.target.value)}
                                className="min-h-[120px]"
                              />
                            </label>
                          ))}
                        </div>

                        <div className="flex flex-wrap items-center gap-4">
                          <label className="flex w-full max-w-[200px] flex-col gap-2 text-sm font-medium text-foreground">
                            <span>Importance</span>
                            <Input
                              type="number"
                              min={0}
                              step={1}
                              value={typeof q?.importance === "number" ? q.importance : 0}
                              onChange={(e) =>
                                setQuestionField(
                                  selectedDimIndex,
                                  selectedFactorIndex,
                                  qi,
                                  "importance",
                                  Number(e.target.value) || 0,
                                )
                              }
                            />
                          </label>
                          <label className="flex items-center gap-2 text-sm font-medium text-foreground">
                            <Checkbox
                              checked={!!q?.reverse}
                              onCheckedChange={(checked) =>
                                setQuestionField(selectedDimIndex, selectedFactorIndex, qi, "reverse", !!checked)
                              }
                            />
                            Reverse scored
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </div>
        <DialogFooter className="border-t border-border/60 bg-muted/10 px-6 py-4">
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleSave}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


